import type { FindingSeverityType } from '@terra/shared-schemas-orchestrator';
import type { PostAttackBlockReason } from '@terra/shared-contracts-smoke-test-history';

export type { PostAttackBlockReason };

/**
 * Trace metadata types for neo and neo-post-attack agents
 */

export type TraceLabel =
  | 'EXPLOITED'
  | 'POTENTIAL_SIGNAL'
  | 'SAFE'
  | 'UNPROVEN'
  | 'CONFIRMED_VULNERABILITY'
  | 'SUSPECTED_VULNERABILITY'
  | 'NOT_OBSERVED (NO VULNERABILITY FOUND)'
  | 'BLOCKED';

/**
 * Signal/Signal label types (from orchestrator schema)
 */
export type SignalLabel =
  | 'COLD'
  | 'HOT'
  | 'PASSED'
  | 'EXPLOITED'
  | 'DUPLICATE'
  | 'BLOCKED';

export type TraceConfidence = 'low' | 'medium' | 'high';

export type TraceEvidence = {
  /** Explanation of what this evidence shows (markdown) */
  explanation: string;
  /** The raw HTTP request/response content (legacy, for backwards compatibility) */
  content?: string;
  /** Raw evidence data for non-HTTP evidences (markdown) */
  data?: string;
  type?: string;
  /** Artifact runtimeId — used to look up the full artifact from ArtifactsProvider */
  artifactId?: string;
  /** Tool call id with its provider prefix stripped, as the agent reported it */
  evidenceId?: string;
  /** ID of the tool execution HAR record to fetch and display */
  toolExecutionHarId?: string | null;
  /** Ordered HAR ids of the redirect hops following toolExecutionHarId (the anchor). */
  redirectHarIds?: string[];
};

export type SkipAttackReasonType =
  | 'authentication_failure'
  | 'signal_blocked_by_guardrails'
  | 'resume_max_retries_exceeded'
  | 'provider_content_filter';

export type TraceMetadata = {
  label?: TraceLabel;
  labelRationale?: string;
  confidenceReason?: string;
  confidence?: TraceConfidence;
  analysis?: string;
  conclusion?: string;
  blockReason?: PostAttackBlockReason;
  ambiguityFactor?: string;
  evidences?: TraceEvidence[];
  previousSignalLabel?: SignalLabel;
  newSignalLabel?: SignalLabel;
  /** When attack is skipped (e.g., blocked by guardrails), this contains the reason */
  skipAttackReason?: string;
  /** Type of skip reason for categorization */
  skipAttackReasonType?: SkipAttackReasonType;
};

export type AgentType = 'neo' | 'neo-post-attack';

export type TraceWithMetadata = {
  id: string;
  threadId: string;
  agentName: string;
  agentType: AgentType;
  signalId?: string;
  signal?: {
    id: string;
    description?: string;
    category?: string;
    analysis?: string;
  };
  createdAt: Date;
  metadata?: TraceMetadata;
};

/**
 * Mapping from attack label to signal/signal label
 * This matches the backend mapping in NEO_POST_ATTACK_LABEL_MAP
 */
const ATTACK_LABEL_TO_SIGNAL_LABEL: Record<TraceLabel, SignalLabel> = {
  EXPLOITED: 'EXPLOITED',
  POTENTIAL_SIGNAL: 'HOT',
  SAFE: 'PASSED',
  UNPROVEN: 'COLD',
  CONFIRMED_VULNERABILITY: 'HOT',
  SUSPECTED_VULNERABILITY: 'HOT',
  'NOT_OBSERVED (NO VULNERABILITY FOUND)': 'PASSED',
  BLOCKED: 'BLOCKED',
};

/**
 * Map an attack label to the corresponding signal/signal label
 */
export function mapAttackLabelToSignalLabel(
  attackLabel: TraceLabel,
): SignalLabel {
  return ATTACK_LABEL_TO_SIGNAL_LABEL[attackLabel];
}

/**
 * Get color palette for a signal label
 */
export function getLabelColorPalette(label: TraceLabel | SignalLabel): string {
  switch (label) {
    case 'EXPLOITED':
      return 'red';
    case 'CONFIRMED_VULNERABILITY':
    case 'SUSPECTED_VULNERABILITY':
    case 'POTENTIAL_SIGNAL':
    case 'HOT':
      return 'orange';
    case 'NOT_OBSERVED (NO VULNERABILITY FOUND)':
    case 'SAFE':
    case 'PASSED':
      return 'green';
    case 'UNPROVEN':
    case 'COLD':
      return 'gray';
    case 'BLOCKED':
      return 'purple';
    case 'DUPLICATE':
      return 'blue';
    default:
      return 'gray';
  }
}

/**
 * Get human-readable label text for signal/signal labels
 */
export function getLabelDisplayText(label: TraceLabel | SignalLabel): string {
  switch (label) {
    case 'EXPLOITED':
      return 'Exploited';
    case 'CONFIRMED_VULNERABILITY':
      return 'Confirmed Vulnerability';
    case 'SUSPECTED_VULNERABILITY':
      return 'Suspected Vulnerability';
    case 'NOT_OBSERVED (NO VULNERABILITY FOUND)':
      return 'Not Observed';
    case 'POTENTIAL_SIGNAL':
    case 'HOT':
      return 'Hot';
    case 'SAFE':
    case 'PASSED':
      return 'Passed';
    case 'UNPROVEN':
    case 'COLD':
      return 'Cold';
    case 'BLOCKED':
      return 'Blocked';
    case 'DUPLICATE':
      return 'Duplicate';
    default:
      return label;
  }
}

type VulnerabilityType = 'Hot' | 'Cold' | 'Safe' | 'Vulnerable';

export function mapLabelToVulnerabilityType(
  label: TraceLabel | SignalLabel,
): VulnerabilityType | null {
  switch (label) {
    case 'EXPLOITED':
      return 'Vulnerable';
    case 'CONFIRMED_VULNERABILITY':
    case 'SUSPECTED_VULNERABILITY':
    case 'POTENTIAL_SIGNAL':
    case 'HOT':
      return 'Hot';
    case 'NOT_OBSERVED (NO VULNERABILITY FOUND)':
    case 'SAFE':
    case 'PASSED':
      return 'Safe';
    case 'UNPROVEN':
    case 'COLD':
      return 'Cold';
    default:
      return null;
  }
}

export function mapLabelToSeverity(
  label: TraceLabel | SignalLabel,
): FindingSeverityType | null {
  switch (label) {
    case 'EXPLOITED':
      return 'Critical';
    case 'CONFIRMED_VULNERABILITY':
    case 'SUSPECTED_VULNERABILITY':
    case 'POTENTIAL_SIGNAL':
    case 'HOT':
      return 'High';
    case 'NOT_OBSERVED (NO VULNERABILITY FOUND)':
      return 'Low';
    case 'BLOCKED':
      return 'Medium';
    case 'SAFE':
    case 'PASSED':
      return 'Low';
    case 'UNPROVEN':
    case 'COLD':
      return 'Info';
    default:
      return null;
  }
}

/**
 * Check if an agent type supports post-attack metadata
 */
export function supportsPostAttackMetadata(agentName?: string): boolean {
  return agentName === 'neo-post-attack';
}
